import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreateDispatchPlace } from '@/features/dispatch-places/dispatch-places.queries';

import type { NewProviderPayload } from '@/features/providers/providers.types';

/**
 * Known providers seeded locally.
 * TODO: replace with a real GET /admin/providers query once that endpoint exists.
 */
const KNOWN_PROVIDERS = [
  { id: '99minutos', name: '99minutos' },
  { id: 'mbe', name: 'MBE México' },
  { id: 'easybox', name: 'Easybox' },
];

/**
 * All fields are strings because HTML <input> always produces strings.
 * Numeric fields (capacity, latitude, longitude) are validated as numeric
 * strings here and converted to numbers when building the API payload in
 * onSubmit. This avoids the TS2322 incompatibility between z.coerce /
 * z.preprocess (which produce `unknown` input type in Zod 4) and the
 * Resolver generic that @hookform/resolvers infers from the schema.
 */
const schema = z.object({
  // ── Generales ────────────────────────────────────────────────────────────
  name: z.string().min(2, 'El nombre es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  type: z.enum(['physical', 'locker', 'virtual']),
  country_code: z.string().length(3, 'Selecciona un país'),
  status: z.enum(['active', 'inactive', 'pending']),
  capacity: z
    .string()
    .min(1, 'La capacidad es requerida')
    .refine((value) => !isNaN(parseInt(value, 10)) && parseInt(value, 10) >= 0, {
      message: 'Debe ser un número mayor o igual a 0',
    }),

  // ── Provider ─────────────────────────────────────────────────────────────
  provider_id: z.string().min(1, 'El ID del proveedor es requerido'),
  provider_name: z.string().min(1, 'El nombre del proveedor es requerido'),

  // ── Ubicación ────────────────────────────────────────────────────────────
  latitude: z
    .string()
    .min(1, 'La latitud es requerida')
    .refine((value) => !isNaN(parseFloat(value)) && Math.abs(parseFloat(value)) <= 90, {
      message: 'Latitud inválida (-90 a 90)',
    }),
  longitude: z
    .string()
    .min(1, 'La longitud es requerida')
    .refine((value) => !isNaN(parseFloat(value)) && Math.abs(parseFloat(value)) <= 180, {
      message: 'Longitud inválida (-180 a 180)',
    }),

  // ── Dirección ────────────────────────────────────────────────────────────
  address_street: z.string().min(1, 'La calle es requerida'),
  address_number: z.string().min(1, 'El número es requerido'),
  address_neighborhood: z.string().min(1, 'La colonia es requerida'),
  address_city: z.string().min(1, 'La ciudad es requerida'),
  address_state: z.string().min(1, 'El estado es requerido'),
  address_postal_code: z.string().min(4, 'El CP es requerido'),
  address_country: z.string().min(1, 'El país de la dirección es requerido'),

  // ── Contacto ─────────────────────────────────────────────────────────────
  contact_name: z.string().min(1, 'El nombre de contacto es requerido'),
  contact_phone: z.string().min(7, 'El teléfono es requerido'),
  contact_email: z.string().email('Email inválido'),

  // ── Facturación ──────────────────────────────────────────────────────────
  billing_tax_id_type: z.enum(['rfc', 'nit', 'rut', 'ruc']),
  billing_tax_id_value: z.string().min(1, 'El RFC / ID fiscal es requerido'),
  billing_legal_name: z.string().min(1, 'La razón social es requerida'),
  billing_regime: z.string().min(1, 'El régimen fiscal es requerido'),

  // ── Bancarios ────────────────────────────────────────────────────────────
  bank_account_id_type: z.enum(['clabe', 'account_number', 'iban']),
  bank_account_id_value: z.string().min(10, 'El número de cuenta es requerido'),
  bank_name: z.string().min(1, 'El banco es requerido'),
  bank_holder_name: z.string().min(1, 'El titular es requerido'),
});

export type NewDispatchPlaceFormValues = z.infer<typeof schema>;

export const useNewDispatchPlacePage = () => {
  const navigate = useNavigate();
  const mutation = useCreateDispatchPlace();

  const [provider_search, setProviderSearch] = useState('99minutos');
  const [is_register_modal_open, setIsRegisterModalOpen] = useState(false);

  const form = useForm<NewDispatchPlaceFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: 'physical',
      country_code: 'MEX',
      status: 'pending',
      capacity: '0',
      billing_tax_id_type: 'rfc',
      bank_account_id_type: 'clabe',
      address_country: 'México',
      provider_id: '99minutos',
      provider_name: '99minutos',
    },
  });

  const filtered_providers = KNOWN_PROVIDERS.filter((provider) =>
    provider.name.toLowerCase().includes(provider_search.toLowerCase()),
  );

  const onSelectProvider = (id: string, name: string) => {
    form.setValue('provider_id', id);
    form.setValue('provider_name', name);
    setProviderSearch(name);
  };

  const onRegisterProvider = (payload: NewProviderPayload) => {
    const new_id = payload.fiscal.name.toLowerCase().replace(/\s+/g, '_');
    onSelectProvider(new_id, payload.fiscal.name);
    setIsRegisterModalOpen(false);
  };

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(
      {
        name: values.name,
        description: values.description,
        type: values.type,
        country_code: values.country_code,
        capacity: parseInt(values.capacity, 10),
        provider: {
          id: values.provider_id,
          name: values.provider_name,
        },
        location: {
          type: 'Point',
          coordinates: [parseFloat(values.longitude), parseFloat(values.latitude)],
        },
        address: {
          street: values.address_street,
          number: values.address_number,
          neighborhood: values.address_neighborhood,
          city: values.address_city,
          state: values.address_state,
          postal_code: values.address_postal_code,
          country: values.address_country,
        },
        contact: {
          name: values.contact_name,
          phone: values.contact_phone,
          email: values.contact_email,
        },
        billing: {
          tax_id_type: values.billing_tax_id_type,
          tax_id_value: values.billing_tax_id_value,
          legal_name: values.billing_legal_name,
          regime: values.billing_regime,
        },
        bank_account: {
          account_id_type: values.bank_account_id_type,
          account_id_value: values.bank_account_id_value,
          bank_name: values.bank_name,
          holder_name: values.bank_holder_name,
        },
      },
      {
        onSuccess: () => {
          toast.success('Punto de despacho creado correctamente');
          navigate('/dispatch-places');
        },
        onError: () => {
          toast.error('No se pudo crear el punto. Intenta de nuevo.');
        },
      },
    );
  });

  return {
    form,
    is_submitting: mutation.isPending,
    is_register_modal_open,
    provider_search,
    filtered_providers,
    onSelectProvider,
    onRegisterProvider,
    onOpenRegisterModal: () => setIsRegisterModalOpen(true),
    onCloseRegisterModal: () => setIsRegisterModalOpen(false),
    onProviderSearchChange: setProviderSearch,
    onSubmit,
  };
};
